import { useCallback, useEffect, useRef, useState } from 'react'

const kFollowThresholdPx = 40
/** How long a jump counts as in flight: long enough for the scroll, and for the layout around it to settle. */
const kElementScrollSettleMs = 1000

/** Whether the container counts as following the stream when scrolled to `scrollTop`. */
function isFollowingAt(container: HTMLDivElement, scrollTop: number): boolean {
  return container.scrollHeight - scrollTop - container.clientHeight <= kFollowThresholdPx
}

/** How far the container has to scroll to put `element` in its middle, capped at where it can rest. */
function centeredScrollTop(container: HTMLDivElement, element: Element): number {
  const containerRect = container.getBoundingClientRect()
  const elementRect = element.getBoundingClientRect()
  const centeredTop = container.scrollTop + (elementRect.top - containerRect.top) - (container.clientHeight - elementRect.height) / 2
  return Math.min(Math.max(centeredTop, 0), Math.max(0, container.scrollHeight - container.clientHeight))
}

/**
 * Keeps the messages container pinned to the bottom while content grows — streamed text,
 * work-log steps, follow-up questions, artifact cards — but stops following as soon as the
 * user scrolls up to read history. Following resumes when they scroll back near the bottom.
 *
 * `contentRef` goes on the element that actually grows (the message list wrapper), not the
 * scrolling container itself, so a ResizeObserver can catch every source of height change —
 * including ones driven by state outside the message list, like follow-up questions or
 * artifact status — without the caller having to enumerate them.
 *
 * Bind `handleScroll` and `handleUserScrollInput` to the container's scroll and wheel/touch handlers, so
 * scrolling the user drives is told apart from scrolling this hook does.
 *
 * Also surfaces whether the user is scrolled away from the bottom, so callers can show a
 * "scroll to bottom" affordance, a `scrollToBottom` action that jumps back down and resumes
 * following, and a `scrollElementIntoView` action for scrolling to a specific message.
 */
export function useFollowStreamScroll() {
  const containerElRef = useRef<HTMLDivElement | null>(null)
  const followRef = useRef(true)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  // True mid-flight during a smooth scrollToBottom, so the positions it passes through are not read
  // as the user leaving the bottom.
  const isAutoScrollingRef = useRef(false)
  // Where the last pin to the bottom left the container, so its own scroll event is not read as the user
  // leaving the bottom once later content has moved the bottom further down.
  const pinnedScrollTopRef = useRef<number | null>(null)
  // True mid-flight during a smooth scroll to an element, so the positions it passes through on the
  // way are not read as the user following the stream.
  const isScrollingToElementRef = useRef(false)
  // The element a jump is aiming at, held for as long as the jump is settling.
  const scrollTargetRef = useRef<Element | null>(null)
  const elementScrollSettleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [showScrollToBottomButton, setShowScrollToBottomButton] = useState(false)
  const prependAnchorRef = useRef<{ element: Element; top: number } | null>(null)

  const handleScroll = useCallback(() => {
    const container = containerElRef.current
    if (!container || isScrollingToElementRef.current) return
    const anchor = prependAnchorRef.current
    if (anchor) anchor.top = anchor.element.getBoundingClientRect().top

    // Only the next scroll event can be the pin's own, so the position is used up whether or not it matches.
    const pinnedScrollTop = pinnedScrollTopRef.current
    pinnedScrollTopRef.current = null
    if (container.scrollTop === pinnedScrollTop) return

    const isFollowing = isFollowingAt(container, container.scrollTop)
    if (isFollowing) isAutoScrollingRef.current = false
    if (isAutoScrollingRef.current) return
    followRef.current = isFollowing
    setShowScrollToBottomButton(!isFollowing)
  }, [])

  const prepareForPrepend = useCallback((element: Element | null) => {
    if (!element) return
    prependAnchorRef.current = { element, top: element.getBoundingClientRect().top }
    followRef.current = false
    isAutoScrollingRef.current = false
    pinnedScrollTopRef.current = null
  }, [])

  const restoreAfterPrepend = useCallback(() => {
    const container = containerElRef.current
    const anchor = prependAnchorRef.current
    prependAnchorRef.current = null
    if (!container || !anchor || !container.contains(anchor.element)) return
    container.scrollTop += anchor.element.getBoundingClientRect().top - anchor.top
    pinnedScrollTopRef.current = container.scrollTop
    setShowScrollToBottomButton(!isFollowingAt(container, container.scrollTop))
  }, [])

  // Scrolling by wheel or touch is the user taking over, which cancels an auto-scroll in flight and retires
  // the last pin position.
  const handleUserScrollInput = useCallback(() => {
    isAutoScrollingRef.current = false
    pinnedScrollTopRef.current = null
  }, [])

  /**
   * Scroll the element a jump is aiming at into the middle of the container.
   *
   * The container scrolls itself, since scrollIntoView on the element would scroll its ancestors too.
   */
  const scrollToTarget = useCallback(() => {
    const container = containerElRef.current
    const target = scrollTargetRef.current
    if (!container || !target) return
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    container.scrollTo({ top: centeredScrollTop(container, target), behavior: prefersReduced ? 'auto' : 'smooth' })
  }, [])

  const contentRef = useCallback(
    (node: HTMLDivElement | null) => {
      resizeObserverRef.current?.disconnect()
      resizeObserverRef.current = null
      if (!node) return

      const observer = new ResizeObserver(() => {
        const container = containerElRef.current
        if (!container) return

        // While a jump is settling, layout around the chat keeps moving (collapsing the side nav
        // animates the chat's width for half a second, rewrapping the transcript under it), so follow
        // the element to where it is now instead of landing where it used to be.
        const scrollTarget = scrollTargetRef.current
        if (scrollTarget) {
          if (container.contains(scrollTarget)) scrollToTarget()
          return
        }

        if (!followRef.current) return
        container.scrollTop = container.scrollHeight
        pinnedScrollTopRef.current = container.scrollTop
      })
      observer.observe(node)
      resizeObserverRef.current = observer
    },
    [scrollToTarget]
  )

  const scrollToBottom = useCallback(() => {
    const container = containerElRef.current
    if (!container) return
    prependAnchorRef.current = null
    followRef.current = true
    isAutoScrollingRef.current = true
    setShowScrollToBottomButton(false)
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    container.scrollTo({ top: container.scrollHeight, behavior: prefersReduced ? 'auto' : 'smooth' })
  }, [])

  /**
   * Center an element in the container, for jumping to a specific message.
   *
   * Following stays off until the scroll lands, or a height change on the way would pin the
   * container back to the bottom.
   */
  const scrollElementIntoView = useCallback(
    (element: Element) => {
      const container = containerElRef.current
      if (!container) return

      // Nothing to scroll (a short chat, or a collapsed chat panel), so leave following on rather than
      // dropping it for a jump that cannot happen.
      const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight)
      if (maxScrollTop === 0 || getComputedStyle(container).visibility === 'hidden') return

      followRef.current = false
      isScrollingToElementRef.current = true
      scrollTargetRef.current = element
      // Scroll events are ignored until the jump lands, so the button is set from where it is headed.
      setShowScrollToBottomButton(!isFollowingAt(container, centeredScrollTop(container, element)))
      if (elementScrollSettleTimeoutRef.current) clearTimeout(elementScrollSettleTimeoutRef.current)
      elementScrollSettleTimeoutRef.current = setTimeout(() => {
        isScrollingToElementRef.current = false
        scrollTargetRef.current = null
        handleScroll()
      }, kElementScrollSettleMs)

      scrollToTarget()
    },
    [handleScroll, scrollToTarget]
  )

  useEffect(() => {
    return () => {
      if (elementScrollSettleTimeoutRef.current) clearTimeout(elementScrollSettleTimeoutRef.current)
    }
  }, [])

  return {
    containerRef: containerElRef,
    contentRef,
    handleScroll,
    handleUserScrollInput,
    showScrollToBottomButton,
    scrollToBottom,
    scrollElementIntoView,
    prepareForPrepend,
    restoreAfterPrepend,
  }
}
